import React, { useState } from 'react';
import './Home.css';
import { Link, useNavigate } from 'react-router-dom';
import Testimonials from './Testimonials';
import CaseStudy from './CaseStudy';
import {
  IconSystems,
  IconVideo,
  IconDesign,
  IconSocial,
  IconSpark,
  IconPin,
  IconTuning,
  IconResults,
} from './Icons';
import bizcardVideo from './bizcard.mp4';

// Our own brand and client design work. The video tile leads because motion
// earns the eye first; the rest are stills from the same body of work.
const CRAFT = [
  { type: 'video', src: bizcardVideo, label: 'Business card', kind: 'Motion' },
  { type: 'image', src: '/SMTnG.png', label: 'Instagram template', kind: 'Social' },
  { type: 'image', src: '/flyer.png', label: 'Event flyer', kind: 'Print' },
  { type: 'image', src: '/SMCTP.png', label: 'Type & palette', kind: 'Identity' },
  { type: 'image', src: '/IG.png', label: 'Instagram post', kind: 'Social' },
  { type: 'image', src: '/1.png', label: 'Card front', kind: 'Print' },
  { type: 'image', src: '/2.png', label: 'Card back', kind: 'Print' },
  { type: 'image', src: '/LogoSM.png', label: 'Logo system', kind: 'Identity' },
];

// The tools we actually reach for, grouped the way we'd describe them out loud.
const STACK = [
  { group: 'Build', items: [
    { src: '/react.svg', name: 'React' },
    { src: '/js.svg', name: 'JavaScript' },
    { src: '/nodejs.svg', name: 'Node.js' },
    { src: '/html.svg', name: 'HTML' },
    { src: '/css.svg', name: 'CSS' },
    { src: '/php.svg', name: 'PHP' },
    { src: '/java.svg', name: 'Java' },
  ]},
  { group: 'Data', items: [
    { src: '/sql.svg', name: 'SQL' },
    { src: '/mongodb.svg', name: 'MongoDB' },
    { src: '/axios.svg', name: 'Axios' },
  ]},
  { group: 'Craft', items: [
    { src: '/davinci.png', name: 'DaVinci' },
    { src: '/blender.svg', name: 'Blender' },
    { src: '/canva.svg', name: 'Canva' },
    { src: '/pixlr.svg', name: 'Pixlr' },
  ]},
];

const SERVICES = [
  {
    id: '01',
    Icon: IconSystems,
    title: 'Websites & Information Systems',
    body:
      'Your website is your storefront — and when you need more, we build the dashboards, internal tools, and custom workflows behind it, so teams can capture and use their data without fighting spreadsheets.',
    topic: 'Website or information system',
    cta: 'Build your storefront',
  },
  {
    id: '02',
    Icon: IconVideo,
    title: 'Video Production',
    body:
      'Concept, shoot, and final cut. Video that tells your story and holds attention — made to work on the platforms your audience actually uses.',
    topic: 'Video production',
    cta: 'Start a video project',
  },
  {
    id: '03',
    Icon: IconDesign,
    title: 'Graphic Design',
    body:
      'Branding and visual identity that make you look like the operation you are. Logos through to marketing collateral, kept cohesive across every surface.',
    topic: 'Graphic design / branding',
    cta: 'Shape your identity',
  },
  {
    id: '04',
    Icon: IconSocial,
    title: 'Social Media Marketing',
    body:
      'Strategy, content, and analytics across Instagram, Facebook, LinkedIn, and TikTok. Campaigns built for genuine engagement rather than vanity numbers.',
    topic: 'Social media marketing',
    cta: 'Grow your channels',
  },
];

const DIFFERENTIATORS = [
  {
    Icon: IconSpark,
    title: 'Fresh ideas',
    body: 'Creative instincts from the generation your customers are already scrolling past.',
  },
  {
    Icon: IconPin,
    title: 'Local roots',
    body: 'Based in Corning–Painted Post. We know the market because we live in it.',
  },
  {
    Icon: IconTuning,
    title: 'Tailored builds',
    body: 'No template drops. Every project is scoped and built for the problem in front of it.',
  },
  {
    Icon: IconResults,
    title: 'Real results',
    body: 'Sites that load fast, rank, and convert — with clients who will vouch for them.',
  },
];

const Home = () => {
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    projectType: '',
  });
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLeadFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsultationClick = () => {
    localStorage.setItem('leadFormData', JSON.stringify(leadFormData));
    navigate('/contact?reason=appointment');
  };

  const formComplete =
    leadFormData.name && leadFormData.email && leadFormData.projectType;

  return (
    <>
      <header className="hero">
        {/* Ambient layers. Both drift against the scroll via [data-parallax]. */}
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-orb hero-orb--a" data-parallax="0.08" aria-hidden="true" />
        <div className="hero-orb hero-orb--b" data-parallax="-0.05" aria-hidden="true" />

        <div className="container hero-inner">
          <p className="eyebrow reveal reveal-fade">
            Corning&ndash;Painted Post, NY &middot; Full-service digital studio
          </p>

          <h1 className="hero-title">
            <span className="reveal-mask">
              <span>Websites and systems</span>
            </span>
            <span className="reveal-mask" style={{ '--reveal-delay': '0.09s' }}>
              <span className="accent-text">that do the work.</span>
            </span>
          </h1>

          <p className="hero-lede reveal reveal-up" style={{ '--reveal-delay': '0.22s' }}>
            We build the websites, custom apps, and data-driven tools that help
            organizations run smarter — plus the video and design that make them
            look the part.
          </p>

          <div className="hero-actions reveal reveal-up" style={{ '--reveal-delay': '0.3s' }}>
            <Link to="/start" className="btn btn--primary btn--lg">
              Get started <span className="btn-arrow">→</span>
            </Link>
            <a
              href="https://calendly.com/shawnysmedia/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--ghost btn--lg"
            >
              Pick a time — 30 min
            </a>
          </div>

          <p className="hero-offer reveal reveal-fade" style={{ '--reveal-delay': '0.36s' }}>
            <span className="hero-offer-badge">$100 credit</span>
            Answer five questions and it comes off your build. Takes about a minute.
          </p>

          {/* Capability strip: reads as a fact bar, not a badge row. */}
          <dl className="hero-strip reveal reveal-up" style={{ '--reveal-delay': '0.4s' }}>
            <div>
              <dt>Discipline</dt>
              <dd>Full-stack development</dd>
            </div>
            <div>
              <dt>Also</dt>
              <dd>Information systems</dd>
            </div>
            <div>
              <dt>And</dt>
              <dd>Video &amp; design</dd>
            </div>
          </dl>
        </div>

        <a className="hero-scroll" href="#services" aria-label="Scroll to services">
          <span className="hero-scroll-label">Scroll</span>
          <span className="hero-scroll-line" aria-hidden="true" />
        </a>
      </header>

      <main className="main-content" id="main">
        <Testimonials />

        {/* ---- Services --------------------------------------------------- */}
        <section className="section section--alt services-section" id="services">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">What we do</p>
              <h2 className="section-title">
                Four disciplines, one team that talks to itself.
              </h2>
              <p className="section-lede">
                Most shops hand you off between vendors. We keep the build, the
                footage, and the brand under one roof — so the pieces actually match.
              </p>
            </div>

            {/* A numbered list, not a card grid: each row opens on hover. */}
            <ul className="service-list" data-stagger="0.07">
              {SERVICES.map(({ id, Icon, title, body, topic, cta }) => (
                <li className="service-row reveal reveal-up" key={id}>
                  <Link
                    to={`/contact?reason=quote&topic=${encodeURIComponent(topic)}`}
                    className="service-row-link"
                  >
                    <span className="service-index">{id}</span>
                    <span className="service-icon">
                      <Icon />
                    </span>
                    <span className="service-body">
                      <span className="service-title">{title}</span>
                      <span className="service-text">{body}</span>
                      <span className="service-cta">
                        {cta} <span className="btn-arrow">→</span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---- Studio ----------------------------------------------------- */}
        <section className="section studio-section">
          <div className="container studio-grid">
            <div className="studio-copy">
              <p className="eyebrow">The studio</p>
              <h2 className="section-title reveal reveal-up">
                A young team that takes the work seriously.
              </h2>
              <div className="reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                <p>
                  ShawnyMedia is a team of high-achieving students out of
                  Corning-Painted Post High School. That means fresh instincts and
                  real hunger — paired with shipped work for a sitting mayor, a
                  charity tournament, a florist, and a landscaping crew.
                </p>
                <p>
                  Our mission is simple: no brand is too small to make a serious
                  impression online. We build like that's true, because it is.
                </p>
              </div>
              <div className="btn-row reveal reveal-up" style={{ '--reveal-delay': '0.16s' }}>
                <Link to="/crew" className="btn btn--ghost">
                  Meet the crew <span className="btn-arrow">→</span>
                </Link>
                <Link to="/contact?reason=appointment" className="btn btn--link">
                  Request a demo <span className="btn-arrow">→</span>
                </Link>
              </div>
            </div>

            <ul className="diff-list" data-stagger="0.06">
              {DIFFERENTIATORS.map(({ Icon, title, body }) => (
                <li className="diff-item reveal reveal-left" key={title}>
                  <span className="diff-icon">
                    <Icon />
                  </span>
                  <span>
                    <span className="diff-title">{title}</span>
                    <span className="diff-text">{body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---- Craft ------------------------------------------------------- */}
        <section className="section section--deep craft-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Design &amp; motion</p>
              <h2 className="section-title reveal reveal-up">
                The look is part of the build.
              </h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Identity, print, social templates, and motion — made in-house, so
                the site and everything around it speak the same language.
              </p>
            </div>
          </div>

          {/* Full-bleed drift. Hovering pauses it so a tile can be read. */}
          <div className="marquee craft-marquee reveal reveal-fade">
            <div className="marquee-track">
              {[...CRAFT, ...CRAFT].map((item, idx) => (
                <figure className="craft-tile" key={`${item.label}-${idx}`} aria-hidden={idx >= CRAFT.length}>
                  <div className="craft-tile-media">
                    {item.type === 'video' ? (
                      /* Only the first copy decodes. The duplicate exists purely
                         to make the loop seamless, so it stays a still frame. */
                      <video
                        src={item.src}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        autoPlay={idx < CRAFT.length}
                      />
                    ) : (
                      <img src={item.src} alt={item.label} loading="lazy" />
                    )}
                  </div>
                  <figcaption>
                    <span className="craft-tile-kind">{item.kind}</span>
                    <span className="craft-tile-label">{item.label}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="container">
            <div className="craft-cta reveal reveal-up">
              <Link to="/projects" className="btn btn--ghost">
                See the design work <span className="btn-arrow">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ---- Stack ------------------------------------------------------- */}
        <section className="section stack-section">
          <div className="container">
            <div className="stack-grid">
              <div className="stack-copy">
                <p className="eyebrow">Under the hood</p>
                <h2 className="section-title reveal reveal-up">What we build with.</h2>
                <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                  No page builders, no locked-in platforms. Real code you own, with
                  the tools chosen to fit the job rather than the other way round.
                </p>
              </div>

              <div className="stack-groups" data-stagger="0.08">
                {STACK.map(({ group, items }) => (
                  <div className="stack-group reveal reveal-up" key={group}>
                    <h3 className="stack-group-name">{group}</h3>
                    <ul className="stack-list">
                      {items.map((tool) => (
                        <li key={tool.name}>
                          <img src={tool.src} alt="" loading="lazy" />
                          {tool.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CaseStudy />

        {/* ---- Lead capture ----------------------------------------------- */}
        <section className="section section--alt lead-section">
          <div className="container lead-grid">
            <div className="lead-copy">
              <p className="eyebrow">Start here</p>
              <h2 className="section-title reveal reveal-up">
                Tell us what you're trying to build.
              </h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Three fields, then a free 30-minute call. We'll tell you what it
                would take — and if we're not the right fit, we'll say so.
              </p>
              <p className="lead-note reveal reveal-fade" style={{ '--reveal-delay': '0.14s' }}>
                No commitment. No pitch deck. Just a straight answer.
              </p>
            </div>

            <form
              className="lead-form surface reveal reveal-scale"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="lead-form-row">
                <div className="lead-field">
                  <label className="field-label" htmlFor="lead-name">
                    Name
                  </label>
                  <input
                    id="lead-name"
                    type="text"
                    name="name"
                    placeholder="Jane Rivera"
                    required
                    className="field"
                    value={leadFormData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="lead-field">
                  <label className="field-label" htmlFor="lead-email">
                    Email
                  </label>
                  <input
                    id="lead-email"
                    type="email"
                    name="email"
                    placeholder="jane@company.com"
                    required
                    className="field"
                    value={leadFormData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="lead-field">
                <label className="field-label" htmlFor="lead-project">
                  What do you need?
                </label>
                <input
                  id="lead-project"
                  type="text"
                  name="projectType"
                  placeholder="A new site for our storefront"
                  required
                  className="field"
                  value={leadFormData.projectType}
                  onChange={handleInputChange}
                />
              </div>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg lead-submit"
                onClick={handleConsultationClick}
                disabled={!formComplete}
              >
                Get your free consultation{' '}
                {formComplete && <span className="btn-arrow">→</span>}
              </button>
            </form>
          </div>
        </section>

        {/* ---- Closing CTA ------------------------------------------------- */}
        <section className="section cta-section">
          <div className="container cta-inner">
            <h2 className="cta-title">
              <span className="reveal-mask">
                <span>Ready when you are.</span>
              </span>
            </h2>
            <p className="cta-text reveal reveal-up" style={{ '--reveal-delay': '0.1s' }}>
              Let's work out what your business actually needs — then build it.
            </p>
            <div className="btn-row cta-actions reveal reveal-up" style={{ '--reveal-delay': '0.18s' }}>
              <Link to="/start" className="btn btn--primary btn--lg">
                Get started — $100 credit <span className="btn-arrow">→</span>
              </Link>
              <Link to="/live-apps" className="btn btn--ghost btn--lg">
                See live work
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
